// Single source of truth for "what does section X of this reading module
// actually contain, in what order" — used by TestSelfAssessment.jsx (the
// learner's own Test screen) and by Admin > Recitations (reviewing a past
// submission), so the two can never drift apart on what counts as a
// section's content. `section` falsy means "no section filter" (the whole
// module, e.g. a module with no sub-divisions).
export function deriveSectionContent(module, section) {
  const items = module.items || [];
  const inSection = (idx) => !section || items[idx]?.rule === section;
  const sectionGroups = (module.groups || []).filter(
    (g) => (g.item_indices || []).length > 0 && g.item_indices.every(inSection)
  );
  const visibleItems = items.map((it, i) => ({ it, i })).filter(({ i }) => inSection(i));
  return { sectionGroups, visibleItems };
}

// Renders exactly what TestSelfAssessment.jsx's own reading-content grid
// renders — same grouped-lines-vs-flat-items layout, same original order,
// same Arabic text. Deliberately excludes the surrounding Test-page chrome
// (parchment card, "Read the passage" label, breath hint, font-size
// controls) so each consumer keeps its own container.
export default function SectionContent({
  module,
  section,
  fontSizeClass = "text-2xl sm:text-3xl",
  groupFontSizeClass = "text-3xl sm:text-4xl",
}) {
  const { sectionGroups, visibleItems } = deriveSectionContent(module, section);

  if (sectionGroups.length === 0 && visibleItems.length === 0) {
    return <div className="text-xs text-[#4B5563] italic">No module content found.</div>;
  }

  return sectionGroups.length > 0 ? (
    <div className="flex flex-col gap-3" data-testid="arabic-content-area-grouped">
      {sectionGroups.map((g) => (
        <div key={g.id} className="rounded-2xl bg-[#FDFBF7] border-2 border-[#E7E0D2] p-5 sm:p-6" data-testid={`test-line-${g.id}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] mb-2" dir="ltr">{g.label}</div>
          <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap py-3 min-h-[4.5rem] sm:min-h-[5.5rem]" dir="rtl">
            {g.item_indices.map((idx) => (
              <span key={idx} className={`font-quran ${groupFontSizeClass} text-[#1F2937] leading-loose flex-shrink-0`} dir="rtl" lang="ar">{module.items[idx]?.ar}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="flex flex-wrap gap-3" style={{ direction: "rtl" }} data-testid="arabic-content-area">
      {visibleItems.map(({ it, i }) => (
        <div key={`${it.ar}-${i}`} data-testid={`item-${i}`} className="rounded-2xl bg-[#FDFBF7] border-2 border-[#E7E0D2] p-5 text-center basis-[calc(50%-0.375rem)] sm:basis-[calc(33.333%-0.5rem)] lg:basis-[calc(25%-0.5625rem)] grow-0 shrink-0">
          <div className={`font-quran ${fontSizeClass} text-[#1F2937] leading-loose`} dir="rtl" lang="ar">{it.ar}</div>
        </div>
      ))}
    </div>
  );
}
